import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {DynamicLabelExtra} from 'src/action-creators/dynamic-labels';
import type {SuggestionProps} from 'src/components/lib/token-list-input/new-stuff';

import {useSelector} from 'react-redux';
import {
  entityColorMapping,
  getAttributeRelationshipPath,
  generateAttributeGrammarTokens,
} from 'src/utils/entities';

import {classify} from 'src/utils';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';

import common from 'src/styles/common.css';
import css from 'src/components/lib/variable-picker/variable-picker.css';


export function NewVariableDropdownRow({
  className,
  option: dynamicLabel,
  onClick,
  isSelected,
}: SuggestionProps<DynamicLabelExtra>): React.ReactElement<
  React.ComponentProps<'div'>
> {
  const entitiesByName = useSelector(selectEntityTypeMappingsByName);
  const {t} = useLabelTranslation();

  let children;
  // @ts-expect-error - TS1501 - This regular expression flag is only available when targeting 'es2018' or later.
  const regex = /^(?=.*[,\n]).+$/s;

  if (!dynamicLabel.entity_type) {
    if (dynamicLabel.arbitrary) {
      children = regex.test(dynamicLabel.value)
        ? `${t('Create_as_single_value', 'Create as single value')} (${
            dynamicLabel.value
          })`
        : `${t('Create', 'Create')} ${dynamicLabel.value}`;
    } else if (dynamicLabel.multiArbitrary) {
      children = `${t('Split', 'Split')} ${dynamicLabel.value
        .trim()
        .split(/[\,\n]/)
        // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
        .filter((value) => value !== '')
        // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
        .map((value) => `<${value}>`)
        .join(' ')}`;
    } else {
      children = (
        <div className={css.variableName}>
          {`${dynamicLabel.label || dynamicLabel.value}`}
        </div>
      );
    }
  } else {
    const color =
      (dynamicLabel.display_entity_type &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly internal_employee: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; readonly ab_web_applicant: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; ... 24 more ...; readonly job_match_variable: { ...'.
        common[entityColorMapping[dynamicLabel?.sense_name]?.text]) ||
      common.colorGray6;

    const entityRelationshipPath =
      dynamicLabel.entityRelationshipPath ??
      getAttributeRelationshipPath(
        dynamicLabel.label,
        dynamicLabel.entity_type,
        entitiesByName,
      );

    const tokens = generateAttributeGrammarTokens(entityRelationshipPath);
    children = (
      <>
        <div
          key={dynamicLabel.label}
          style={{
            color,
          }}
          className={css.variableName}
        >
          {`${entityRelationshipPath[0]}`}
        </div>

        <section className={css.dropdownPipeSeparator} role="presentation">
          |
        </section>
        {tokens.map((token, index) => (
          <div
            key={index} // eslint-disable-line react/no-array-index-key
            className={classify(css.dropdownPhraseElement, {
              [css.dropdownBoldPhraseElement]: index % 2 !== 0,
            })}
          >
            {token}
          </div>
        ))}
      </>
    );
  }

  return (
    <div
      className={classify(css.dropdownResult, {
        [css.dropdownResultHighlighted]: isSelected,
      })}
      onClick={onClick}
      data-qa-id="variable-picker-dropdown-row"
    >
      {children}
    </div>
  );
}
